import type { ReactNode } from 'react';
import { Button } from 'components/Button';
import Modal from 'components/modals/Modal';
import CloseButton from './CloseButton';
import Warning3dIllustration from './Warning3dIllustration';

interface Props {
  isOpen: boolean;
  /** Go back / X / backdrop: dismisses without acting. */
  onClose: () => void;
  title: ReactNode;
  description: string;
  /** Replaces the 3D warning art — e.g. the request-limit cage. */
  illustration?: ReactNode;
  /** The dismissing action's label; "Go back" unless the design names the surface. */
  cancelLabel?: string;
  confirmLabel: string;
  onConfirm: () => void;
  /** `primary` for a navigation (Figma's dark pill); `brand` for a destructive act. */
  confirmVariant?: 'brand' | 'primary';
  isConfirming?: boolean;
}

/** Figma "Warning": 3D warning art, title, one line of consequence, Go back + brand confirm. */
export default function WarningConfirmModal({
  isOpen,
  onClose,
  title,
  description,
  illustration,
  cancelLabel = 'Go back',
  confirmLabel,
  onConfirm,
  confirmVariant = 'brand',
  isConfirming = false,
}: Props) {
  return (
    <Modal
      isOpen={isOpen}
      handleClose={onClose}
      classNameMaxWidth="max-w-md lg:max-w-[40rem]"
      classNameRounded="rounded-t-3xl sm:rounded-2xl lg:rounded-3xl"
      classNameHeight="max-h-[90dvh]"
    >
      <div className="flex max-h-[90dvh] flex-col bg-color-bg-lightmode-primary dark:bg-color-bg-darkmode-primary">
        <header className="flex shrink-0 items-center justify-end p-ds-lg lg:px-ds-3xl lg:py-5">
          <CloseButton onClose={onClose} />
        </header>
        <div className="flex min-h-0 flex-1 flex-col items-center gap-ds-lg overflow-y-auto px-ds-lg pb-ds-2xl pt-ds-sm text-center lg:gap-ds-2xl lg:px-ds-3xl lg:pb-16">
          {illustration ?? <Warning3dIllustration className="h-[7.5rem] w-[7.5rem]" />}
          {/* `contents` keeps the mobile column flat; a real text block at lg. */}
          <div className="contents lg:flex lg:w-full lg:flex-col lg:items-center lg:gap-ds-xl">
            <h2 className="typography-product-display text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
              {title}
            </h2>
            <p className="typography-product-body max-w-[21.5rem] text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary lg:max-w-none">
              {description}
            </p>
          </div>
        </div>
        <footer className="flex shrink-0 flex-col gap-ds-md border-t border-color-border-input-lightmode px-ds-lg pb-ds-lg pt-ds-lg dark:border-color-border-input-darkmode lg:flex-row lg:gap-ds-xl lg:px-ds-3xl lg:py-5">
          <Button
            variant="secondary"
            size="lg"
            onClick={onClose}
            disabled={isConfirming}
            className="lg:min-w-0 lg:flex-1"
          >
            {cancelLabel}
          </Button>
          <Button
            variant={confirmVariant}
            size="lg"
            onClick={onConfirm}
            loading={isConfirming}
            className="lg:min-w-0 lg:flex-1"
          >
            {confirmLabel}
          </Button>
        </footer>
      </div>
    </Modal>
  );
}
